import { useEffect, useMemo, useState } from "react";
import { Loader2, LockKeyhole, Search } from "lucide-react";

import { artists } from "@/data/artists";
import { useCatalogVersion } from "@/data/catalogLive";
import { salas } from "@/data/salas";
import { invalidateArtworkStatusCache } from "@/hooks/useArtworkStatus";
import AdminHeader from "@/components/latir/AdminHeader";
import { useSEO } from "@/hooks/useSEO";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "arteconciencia_admin_token_v1"; // shared with AdminVisitas

// slug → { number, title }  — computed once at module scope.
const SALA_BY_SLUG = (() => {
  const m = {};
  for (const s of salas) {
    for (const slug of s.artworkSlugs || []) {
      m[slug] = { number: s.number, title: s.title };
    }
  }
  return m;
})();

async function apiFetch(path, token, options = {}) {
  const res = await fetch(`${API}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  if (res.status === 401) {
    localStorage.removeItem(TOKEN_KEY);
    window.location.reload();
    return;
  }
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) throw new Error((data && data.detail) || `Error ${res.status}`);
  return data;
}

export default function AdminObras() {
  useCatalogVersion();
  useSEO({ title: "Estado de venta de obras | Admin Arteconciencia", noindex: true });
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  if (!token) {
    return (
      <Login
        onLogin={(t) => {
          localStorage.setItem(TOKEN_KEY, t);
          setToken(t);
        }}
      />
    );
  }
  return (
    <Dashboard
      token={token}
      onLogout={() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      }}
    />
  );
}

/* ---------------- Login (reuses visits admin token) ---------------- */
function Login({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/visits/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Contraseña incorrecta");
      onLogin(data.token);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="paper-grain min-h-screen bg-paper flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm text-center" data-testid="admin-obras-login">
        <p className="eyebrow text-latir-red mb-6">Arteconciencia · Admin</p>
        <LockKeyhole className="w-6 h-6 text-ink-mute mx-auto mb-4" strokeWidth={1.4} />
        <h1
          className="font-display text-[2rem] text-ink"
          style={{ fontFamily: '"Instrument Serif", serif' }}
        >
          Panel de obras
        </h1>
        <p className="mt-3 font-editorial italic text-[1rem] text-ink-soft">
          Ingresa la contraseña administrativa.
        </p>
        <input
          type="password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Contraseña"
          className="mt-8 w-full bg-transparent border-b border-line focus:border-latir-red outline-none text-center font-ui text-[1rem] py-3 tracking-[0.1em]"
          data-testid="admin-obras-password"
        />
        {error && (
          <p
            className="mt-3 font-ui uppercase text-[0.68rem] tracking-[0.2em] text-latir-red"
            data-testid="admin-obras-login-error"
          >
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={busy || !password}
          className="mt-8 inline-flex items-center gap-3 bg-ink text-paper-raised px-6 py-3 font-ui uppercase tracking-[0.2em] text-[0.72rem] hover:bg-latir-red transition-colors disabled:opacity-50"
          data-testid="admin-obras-login-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Entrar"}
        </button>
      </form>
    </div>
  );
}

/* ------------------------- Dashboard --------------------------- */
function Dashboard({ token, onLogout }) {
  const [statusBySlug, setStatusBySlug] = useState({}); // slug → "sold" | "available"
  const [savingSlug, setSavingSlug] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [loaded, setLoaded] = useState(false);

  // Load current statuses from backend.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await apiFetch("/api/admin/artwork-status", token);
        if (!alive) return;
        const map = {};
        for (const item of data.items || []) map[item.slug] = item.sale_status;
        setStatusBySlug(map);
      } catch (e) {
        setError(e.message);
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  const rows = useMemo(() => {
    // Sort artworks alphabetically by artist name (accent-insensitive).
    const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    const sorted = [...artists].sort((a, b) => norm(a.name).localeCompare(norm(b.name)));
    const query = q.trim().toLowerCase();
    if (!query) return sorted;
    return sorted.filter(
      (a) =>
        (a.name || "").toLowerCase().includes(query) ||
        (a.title || "").toLowerCase().includes(query) ||
        (a.slug || "").toLowerCase().includes(query),
    );
  }, [q]);

  const isSold = (slug) => (statusBySlug[slug] || "available") === "sold";
  const soldCount = Object.values(statusBySlug).filter((v) => v === "sold").length;

  const toggle = async (slug, next) => {
    setSavingSlug(slug);
    setError(null);
    try {
      await apiFetch("/api/admin/artwork-status", token, {
        method: "POST",
        body: JSON.stringify({ slug, sale_status: next ? "sold" : "available" }),
      });
      setStatusBySlug((prev) => ({ ...prev, [slug]: next ? "sold" : "available" }));
      invalidateArtworkStatusCache();
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingSlug(null);
    }
  };

  return (
    <div className="paper-grain min-h-screen bg-paper" data-testid="admin-obras-dashboard">
      <AdminHeader onLogout={onLogout} />
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8">
        {/* Header */}
        <div className="flex items-baseline justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="eyebrow text-latir-red">Arteconciencia · Admin</p>
            <h1
              className="font-display text-[2.2rem] text-ink mt-1"
              style={{ fontFamily: '"Instrument Serif", serif' }}
            >
              Estado de venta de obras
            </h1>
            <p className="font-editorial italic text-[1rem] text-ink-soft mt-1">
              {artists.length} obras · {soldCount} marcadas como vendidas
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-mute" strokeWidth={1.4} />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por artista, título o slug…"
            className="w-full pl-10 pr-4 py-3 bg-paper-raised border border-line focus:border-latir-red rounded-[3px] outline-none font-ui text-[0.9rem]"
            data-testid="admin-obras-search"
          />
        </div>

        {error && (
          <p
            className="mb-4 font-ui uppercase text-[0.68rem] tracking-[0.2em] text-latir-red"
            data-testid="admin-obras-error"
          >
            {error}
          </p>
        )}

        {!loaded ? (
          <p className="text-center py-16 text-ink-mute">
            <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando…
          </p>
        ) : (
          <ul className="divide-y divide-line border-y border-line" data-testid="admin-obras-list">
            {rows.map((a) => {
              const sold = isSold(a.slug);
              const saving = savingSlug === a.slug;
              return (
                <li
                  key={a.slug}
                  className="flex items-center gap-4 py-4"
                  data-testid={`admin-obra-row-${a.slug}`}
                >
                  <div className="w-14 h-14 shrink-0 bg-paper-deep overflow-hidden rounded-[3px] flex items-center justify-center">
                    {a.image && !String(a.image).includes("placeholder") ? (
                      <img src={a.image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-[0.55rem] text-ink-mute font-ui uppercase tracking-widest">s/f</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-[1.05rem] text-ink truncate">{a.name}</p>
                    <p className="font-editorial italic text-[0.95rem] text-ink-soft truncate">
                      {a.title || "—"}
                    </p>
                    <div className="flex items-center gap-3 flex-wrap mt-1">
                      <span
                        className={
                          "font-ui uppercase tracking-[0.2em] text-[0.62rem] px-2 py-0.5 rounded-[3px] " +
                          (sold
                            ? "bg-latir-red text-paper"
                            : "bg-emerald-600 text-paper")
                        }
                        data-testid={`admin-obra-status-${a.slug}`}
                      >
                        {sold ? "Vendida" : "Disponible"}
                      </span>
                      {a.price && (
                        <span className="font-ui text-[0.68rem] tracking-[0.06em] text-ink">
                          {a.price}
                        </span>
                      )}
                      {SALA_BY_SLUG[a.slug] && (
                        <span className="font-ui text-[0.62rem] tracking-[0.15em] uppercase text-ink-mute">
                          Sala {SALA_BY_SLUG[a.slug].number} · {SALA_BY_SLUG[a.slug].title}
                        </span>
                      )}
                    </div>
                  </div>
                  <label
                    className="inline-flex items-center gap-2 cursor-pointer select-none shrink-0"
                    data-testid={`admin-obra-toggle-${a.slug}`}
                  >
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={sold}
                      disabled={saving}
                      onChange={(e) => toggle(a.slug, e.target.checked)}
                    />
                    <span
                      className={
                        "px-3 py-1.5 rounded-[3px] font-ui uppercase tracking-[0.2em] text-[0.68rem] transition-colors " +
                        (sold
                          ? "bg-latir-red text-paper"
                          : "bg-paper-raised text-ink border border-line")
                      }
                    >
                      {saving ? "…" : sold ? "Vendida" : "Marcar vendida"}
                    </span>
                  </label>
                </li>
              );
            })}
            {rows.length === 0 && (
              <li className="py-10 text-center font-editorial italic text-ink-mute">
                Sin resultados.
              </li>
            )}
          </ul>
        )}

        <p className="mt-10 font-ui text-[0.7rem] tracking-[0.15em] text-ink-mute leading-relaxed">
          El cambio se guarda en el momento y aparece automáticamente en las fichas y el
          Recorrido de LATIR. Recarga la página pública si estás mirando otra pestaña.
        </p>
      </div>
    </div>
  );
}
